import { shallow, useStore } from '@tanstack/react-store'
import { AlertTriangleIcon, InfoIcon, MapPinIcon, SearchIcon } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import * as m from '~/paraglide/messages'
import { countryToIsoCode, normalizeCountry } from '~/shared/lib/countries'
import { createFieldValidators } from '~/shared/lib/form-utils'
import { useGeocoding } from '~/shared/lib/useGeocoding'
import type { Listing } from '~/shared/types'
import { eventLocationSchema } from '~/shared/types'
import type { GeocodingSuggestion } from '~/shared/types/geoCoding'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/shared/ui/card'
import { Checkbox } from '~/shared/ui/checkbox'
import { CountrySelect } from '~/shared/ui/country-select'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/shared/ui/select'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/shared/ui/tooltip'
import { AddressHiddenField } from './address-hidden-field'

interface EventLocationSectionProps {
  // biome-ignore lint/suspicious/noExplicitAny: TanStack Form API
  form: any
  listings?: Listing[]
  selectedListingId?: string | null
  onListingChange?: (listingId: string | null) => void
  isEditMode?: boolean
  linkedListingName?: string | null
  /** The AI found no place, or one it could not pin: ask for it instead of guessing. */
  notice?: 'missing' | 'unconfirmed' | null
  savedConfirmation?: boolean
}

interface LocationFormState {
  values: {
    location?: {
      name?: string | null
      street?: string
      city?: string
      country?: string
      coordinates?: unknown
    }
    locationPending?: boolean
  }
}

interface WatchedLocation {
  name: string
  street: string
  city: string
  country: string
  pinned: boolean
  pending: boolean
}

const ADDRESS_FIELDS = ['location.street', 'location.city', 'location.country'] as const

function isLocationOverridden(
  currentLocation: { street: string; city: string; country: string },
  listingLocation: { street: string; city: string; country: string },
): boolean {
  return (
    currentLocation.street !== listingLocation.street ||
    currentLocation.city !== listingLocation.city ||
    normalizeCountry(currentLocation.country) !== normalizeCountry(listingLocation.country)
  )
}

export function EventLocationSection({
  form,
  listings = [],
  selectedListingId,
  onListingChange,
  isEditMode = false,
  linkedListingName,
  notice = null,
  savedConfirmation,
}: EventLocationSectionProps) {
  const linkToListingId = useId()
  const pendingId = useId()
  const watched: WatchedLocation = useStore(
    form.store,
    (state: unknown) => {
      const { location, locationPending } = (state as LocationFormState).values
      return {
        name: location?.name || '',
        street: location?.street || '',
        city: location?.city || '',
        country: location?.country || '',
        pinned: !!location?.coordinates,
        pending: !!locationPending,
      }
    },
    shallow,
  )
  const addressRequired = !selectedListingId && !watched.pending
  const [streetBlurred, setStreetBlurred] = useState(false)
  const [cityBlurred, setCityBlurred] = useState(false)
  const [countryBlurred, setCountryBlurred] = useState(false)

  const hasListings = listings.length > 0
  const hasSingleListing = listings.length === 1
  const hasMultipleListings = listings.length > 1

  const { suggest, retrieve } = useGeocoding()
  const [suggestions, setSuggestions] = useState<GeocodingSuggestion[]>([])

  const handleSuggest = async (street: string, city: string, country: string, name = '') => {
    const query = [name, street, city].filter(Boolean).join(', ')

    if (!query || query.length < 3) {
      setSuggestions([])
      return
    }

    const results = await suggest({
      query,
      country: countryToIsoCode(country),
      limit: 5,
    })

    setSuggestions(results)
  }

  const suggestedForNotice = useRef(false)
  // biome-ignore lint/correctness/useExhaustiveDependencies: offer the address once, when the AI's unpinned place arrives
  useEffect(() => {
    if (notice !== 'unconfirmed' || suggestedForNotice.current) return
    suggestedForNotice.current = true
    handleSuggest(watched.street, watched.city, watched.country, watched.name)
  }, [notice])

  const unpin = () => form.setFieldValue('location.coordinates', undefined)

  const handlePendingChange = (pending: boolean) => {
    form.setFieldValue('locationPending', pending)
    if (!pending) return
    onListingChange?.(null)
    unpin()
    setSuggestions([])
    form.setFieldMeta('location.street', (meta: object) => ({ ...meta, errorMap: {} }))
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: recheck shown address errors when a value or the required rule changes
  useEffect(() => {
    for (const name of ADDRESS_FIELDS) {
      if (!form.getFieldMeta(name)?.errors?.some(Boolean)) continue
      if (form.fieldInfo[name]?.instance?.options?.validators) {
        form.validateField(name, 'change')
        form.validateField(name, 'blur')
      } else {
        form.setFieldMeta(name, (meta: object) => ({ ...meta, errorMap: {}, errorSourceMap: {} }))
      }
    }
  }, [addressRequired, selectedListingId, watched.street, watched.city, watched.country])

  const visibleNotice =
    notice && !selectedListingId && !watched.pending
      ? notice === 'missing' && !watched.street && !watched.city
        ? 'missing'
        : notice === 'unconfirmed' && !watched.pinned
          ? 'unconfirmed'
          : null
      : null

  const selectedListing = selectedListingId
    ? listings.find((l) => l.id === selectedListingId)
    : null

  const handleListingSelect = (listingId: string | null) => {
    if (onListingChange) {
      onListingChange(listingId)
    }

    if (listingId) {
      form.setFieldValue('locationPending', false)
      const listing = listings.find((l) => l.id === listingId)
      if (listing) {
        form.setFieldValue('location.name', listing.location.name || listing.name || '')
        form.setFieldValue('location.street', listing.location.street)
        form.setFieldValue('location.city', listing.location.city)
        form.setFieldValue('location.country', normalizeCountry(listing.location.country))
        if (listing.location.coordinates) {
          form.setFieldValue('location.coordinates', listing.location.coordinates)
        }
      }
    }
  }

  const handleSingleListingToggle = (checked: boolean) => {
    if (hasSingleListing) {
      handleListingSelect(checked ? listings[0].id : null)
    }
  }

  const handleResetToListingLocation = () => {
    if (selectedListing) {
      form.setFieldValue(
        'location.name',
        selectedListing.location.name || selectedListing.name || '',
      )
      form.setFieldValue('location.street', selectedListing.location.street)
      form.setFieldValue('location.city', selectedListing.location.city)
      form.setFieldValue('location.country', normalizeCountry(selectedListing.location.country))
      if (selectedListing.location.coordinates) {
        form.setFieldValue('location.coordinates', selectedListing.location.coordinates)
      }
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{m.location()}</CardTitle>
        <CardDescription>{m.whereWillTheEventBeHeld()}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {hasListings && !isEditMode && (
          <div className="space-y-2">
            <Label>{m.linkToListing()}</Label>
            {hasSingleListing && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={linkToListingId}
                  checked={selectedListingId === listings[0].id}
                  onCheckedChange={handleSingleListingToggle}
                />
                <label
                  htmlFor={linkToListingId}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  {m.linkEventTo({ listingName: listings[0].name })}
                </label>
              </div>
            )}
            {hasMultipleListings && (
              <Select
                value={selectedListingId || ''}
                onValueChange={(value) => handleListingSelect(value === 'none' ? null : value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={m.selectAListing()} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{m.noListing()}</SelectItem>
                  {listings.map((listing) => (
                    <SelectItem key={listing.id} value={listing.id}>
                      <div className="flex items-center gap-2">
                        <MapPinIcon className="size-4 text-muted-foreground" />
                        <span>{listing.name}</span>
                        <span className="text-muted-foreground text-xs">
                          ({listing.location.city})
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        )}

        {selectedListingId && selectedListing && (
          <form.Subscribe
            selector={(state: {
              values: {
                location?: { street?: string; city?: string; country?: string }
              }
            }) => ({
              street: state.values.location?.street || '',
              city: state.values.location?.city || '',
              country: state.values.location?.country || '',
            })}
          >
            {({ street, city, country }: { street: string; city: string; country: string }) => {
              const hasOverride = isLocationOverridden(
                { street, city, country },
                selectedListing.location,
              )

              if (!hasOverride) {
                return (
                  <p className="text-xs text-muted-foreground">
                    {m.locationPrefilledFromListing()}
                  </p>
                )
              }

              return (
                <div
                  role="alert"
                  className="relative w-full rounded-lg border border-amber-500/50 bg-amber-50 dark:bg-amber-950/20 px-4 py-3 text-sm"
                >
                  <div className="flex items-start gap-3">
                    <AlertTriangleIcon className="size-4 text-amber-600 mt-0.5 shrink-0" />
                    <div className="text-amber-800 dark:text-amber-200">
                      <span>{m.locationOverrideWarning()}</span>
                      <button
                        type="button"
                        onClick={handleResetToListingLocation}
                        className="ml-2 text-amber-700 dark:text-amber-300 underline hover:no-underline font-medium"
                      >
                        {m.resetToListingLocation()}
                      </button>
                    </div>
                  </div>
                </div>
              )
            }}
          </form.Subscribe>
        )}

        {isEditMode && linkedListingName && (
          <div className="space-y-2">
            <Label className="flex items-center gap-1">
              {m.linkedListing()}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <InfoIcon className="size-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <p>{m.listingLinkCannotBeChanged()}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
            <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-md border">
              <MapPinIcon className="size-4 text-muted-foreground" />
              <span className="text-sm">{linkedListingName}</span>
            </div>
          </div>
        )}

        {visibleNotice && (
          <div
            role="status"
            className="flex items-start gap-3 rounded-lg border border-border bg-muted/50 px-4 py-3 text-sm"
          >
            {visibleNotice === 'missing' ? (
              <MapPinIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            ) : (
              <SearchIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            )}
            <span>
              {visibleNotice === 'missing'
                ? m.eventLocationNotFound()
                : m.eventLocationUnconfirmed()}
            </span>
          </div>
        )}

        <div className="flex items-start gap-2">
          <Checkbox
            id={pendingId}
            checked={watched.pending}
            onCheckedChange={(checked) => handlePendingChange(checked === true)}
          />
          <div className="grid gap-1">
            <label htmlFor={pendingId} className="cursor-pointer text-sm font-medium leading-none">
              {m.eventLocationPending()}
            </label>
            {watched.pending && (
              <p className="text-xs text-muted-foreground">{m.eventLocationPendingHint()}</p>
            )}
          </div>
        </div>

        <form.Field name="location.name">
          {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
          {(field: any) => (
            <div className="space-y-2">
              <Label htmlFor={field.name}>{m.venueNameOptional()}</Label>
              <Input
                id={field.name}
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={m.conventionCenterParkNameEtc()}
              />
            </div>
          )}
        </form.Field>

        <form.Field
          name="location.street"
          validators={
            addressRequired ? createFieldValidators(eventLocationSchema.shape.street) : undefined
          }
        >
          {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
          {(field: any) => {
            const shouldShowError =
              (field.state.meta.isTouched || streetBlurred) && field.state.meta.errors

            return (
              <div className="space-y-2">
                <Label htmlFor={field.name}>{m.streetAddress()}</Label>
                <div className="relative">
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={() => {
                      setStreetBlurred(true)
                      field.handleBlur()
                    }}
                    onChange={(e) => {
                      const value = e.target.value
                      field.handleChange(value)
                      unpin()

                      const city = form.state.values.location?.city || ''
                      const country = form.state.values.location?.country || ''

                      handleSuggest(value, city, country)
                    }}
                    placeholder={m.addressPlaceholder()}
                    required={addressRequired}
                  />
                  {suggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 z-50 mt-1 border rounded-md bg-background">
                      <ul>
                        {suggestions.map((s) => (
                          // biome-ignore lint/a11y/useKeyWithClickEvents: suggestion list item
                          <li
                            key={s.mapboxId}
                            className="px-3 py-2 cursor-pointer hover:bg-muted"
                            onClick={async () => {
                              const full = await retrieve({
                                mapboxId: s.mapboxId,
                              })
                              if (!full) return
                              form.setFieldValue('location.street', full.street ?? '')
                              form.setFieldValue('location.city', full.city ?? '')
                              form.setFieldValue(
                                'location.country',
                                normalizeCountry(full.country ?? ''),
                              )
                              form.setFieldValue('location.coordinates', {
                                lat: full.lat,
                                lng: full.lng,
                              })
                              setSuggestions([])
                            }}
                          >
                            <div className="text-sm">{s.name}</div>
                            <div className="text-xs text-muted-foreground">{s.fullAddress}</div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                {shouldShowError && (
                  <p className="text-sm text-destructive">{field.state.meta.errors[0]}</p>
                )}
              </div>
            )
          }}
        </form.Field>

        <div className="gap-4 grid grid-cols-1 xs:grid-cols-2">
          <form.Field
            name="location.city"
            validators={
              selectedListingId ? undefined : createFieldValidators(eventLocationSchema.shape.city)
            }
          >
            {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
            {(field: any) => {
              const shouldShowError =
                (field.state.meta.isTouched || cityBlurred) && field.state.meta.errors

              return (
                <div className="space-y-2">
                  <Label htmlFor={field.name}>{m.city()}</Label>
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={() => {
                      setCityBlurred(true)
                      field.handleBlur()
                    }}
                    onChange={(e) => {
                      field.handleChange(e.target.value)
                      unpin()
                      setSuggestions([])
                    }}
                    required={!selectedListingId}
                  />
                  {shouldShowError && (
                    <p className="text-sm text-destructive">{field.state.meta.errors[0]}</p>
                  )}
                </div>
              )
            }}
          </form.Field>

          <form.Field
            name="location.country"
            validators={
              selectedListingId
                ? undefined
                : createFieldValidators(eventLocationSchema.shape.country)
            }
          >
            {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
            {(field: any) => {
              const shouldShowError =
                (field.state.meta.isTouched || countryBlurred) && field.state.meta.errors

              return (
                <div className="space-y-2">
                  <Label htmlFor={field.name}>{m.country()}</Label>
                  <CountrySelect
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={() => {
                      setCountryBlurred(true)
                      field.handleBlur()
                    }}
                    onChange={(value) => {
                      const changed = value !== normalizeCountry(field.state.value)
                      field.handleChange(value)
                      if (!changed) return
                      unpin()
                      setSuggestions([])
                    }}
                    placeholder={m.selectACountry()}
                    required={!selectedListingId}
                  />
                  {shouldShowError && (
                    <p className="text-sm text-destructive">{field.state.meta.errors[0]}</p>
                  )}
                </div>
              )
            }}
          </form.Field>
        </div>
        <AddressHiddenField form={form} savedConfirmation={savedConfirmation} />
      </CardContent>
    </Card>
  )
}
